import { useQuery } from '@tanstack/react-query';
import type {
  BankAccount,
  Business,
  ChartAccount,
  CostCenter,
  Department,
  Paginated,
  Person,
  StockLocation,
  Unit,
} from '@amai/contracts';
import type { Item } from '@amai/contracts';
import { api } from './api';

/**
 * Consultas de apoio usadas pelos formulários (selects de centro de custo,
 * conta, fornecedor, item, local). Ficam num arquivo só para que a chave de
 * cache seja a mesma em toda tela — trocar um centro de custo numa tela
 * invalida a lista em todas.
 */

export function useCostCenters(onlyAnalytic = true) {
  return useQuery({
    queryKey: ['cost-centers', 'flat'],
    queryFn: () => api.get<{ data: CostCenter[] }>('/cost-centers'),
    select: (response) =>
      onlyAnalytic ? response.data.filter((c) => c.isAnalytic) : response.data,
    staleTime: 5 * 60_000,
  });
}

export function useChartAccounts(type?: 'RECEITA' | 'DESPESA') {
  return useQuery({
    queryKey: ['chart-accounts', 'flat'],
    queryFn: () => api.get<{ data: ChartAccount[] }>('/chart-accounts'),
    select: (response) =>
      response.data.filter((a) => a.isAnalytic && (!type || a.type === type)),
    staleTime: 5 * 60_000,
  });
}

/**
 * Empresas do grupo (AMAI PARK, BELLA CENTER…). Ativas por padrão — é o que
 * os formulários oferecem; o cadastro pede as inativas também.
 */
export function useBusinesses(includeInactive = false) {
  return useQuery({
    queryKey: ['businesses', includeInactive],
    queryFn: () =>
      api.get<{ data: Business[] }>('/businesses', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
    select: (response) => response.data,
    staleTime: 5 * 60_000,
  });
}

/**
 * O plano de contas do jeito que a árvore mostra, para o lançamento.
 *
 * Conta a PAGAR oferece os débitos: despesas, custos, impostos, investimento
 * e as deduções de receita (taxa de cartão, estorno — dinheiro que sai).
 * Conta a RECEBER oferece as receitas. Cada conta vem dentro do grupo-pai,
 * com o mesmo código e nome da tela de Plano de contas — a lista e a árvore
 * têm de bater, senão o operador escolhe "Água" sem saber de qual grupo.
 */
export function useChartAccountGroups(entryType: 'PAYABLE' | 'RECEIVABLE') {
  return useQuery({
    queryKey: ['chart-accounts', 'flat'],
    queryFn: () => api.get<{ data: ChartAccount[] }>('/chart-accounts'),
    staleTime: 5 * 60_000,
    select: (response) => {
      const todas = response.data;
      const porId = new Map(todas.map((conta) => [conta.id, conta]));
      const serve = (conta: ChartAccount) =>
        entryType === 'PAYABLE'
          ? conta.type === 'DESPESA' || conta.nature === 'DEDUCAO'
          : conta.type === 'RECEITA' && conta.nature !== 'DEDUCAO';

      const contas = todas
        .filter((conta) => conta.isAnalytic && conta.isActive && serve(conta))
        .sort((a, b) => a.path.localeCompare(b.path, 'pt-BR', { numeric: true }));

      const grupos: Array<{ label: string; accounts: ChartAccount[] }> = [];
      for (const conta of contas) {
        const pai = conta.parentId ? porId.get(conta.parentId) : undefined;
        const avo = pai?.parentId ? porId.get(pai.parentId) : undefined;
        const label = pai
          ? `${avo ? `${avo.code} ${avo.name} › ` : ''}${pai.code} · ${pai.name}`
          : 'Sem grupo';
        const ultimo = grupos[grupos.length - 1];
        if (ultimo && ultimo.label === label) ultimo.accounts.push(conta);
        else grupos.push({ label, accounts: [conta] });
      }
      return { contas, grupos };
    },
  });
}

/** Clientes — quem PAGA o parque: o "Cliente" da conta a receber. */
export function useCustomers(search?: string) {
  return useQuery({
    queryKey: ['people', 'customers', search],
    queryFn: () =>
      api.get<Paginated<Person>>('/people', {
        role: 'CLIENTE',
        perPage: 100,
        search: search || undefined,
      }),
    select: (response) => response.data,
    staleTime: 60_000,
  });
}

export function useBankAccounts() {
  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<{ data: BankAccount[] }>('/bank-accounts'),
    select: (response) => response.data,
    staleTime: 60_000,
  });
}

export function useStockLocations() {
  return useQuery({
    queryKey: ['stock-locations'],
    queryFn: () => api.get<{ data: StockLocation[] }>('/stock-locations'),
    select: (response) => response.data,
    staleTime: 5 * 60_000,
  });
}

/**
 * Setores. A lista é do RH, mas quem abre requisição também lê — o setor
 * solicitante sai daqui em vez de virar uma segunda lista para manter.
 */
export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<{ data: Department[] }>('/departments'),
    select: (response) => response.data.filter((d) => d.isActive),
    staleTime: 5 * 60_000,
  });
}

export function useUnits() {
  return useQuery({
    queryKey: ['units'],
    queryFn: () => api.get<{ data: Unit[] }>('/units'),
    select: (response) => response.data.filter((u) => u.isActive),
    staleTime: 5 * 60_000,
  });
}

export function useSuppliers(search?: string) {
  return useQuery({
    queryKey: ['people', 'suppliers', search],
    queryFn: () =>
      api.get<Paginated<Person>>('/people', {
        role: 'FORNECEDOR',
        perPage: 100,
        search: search || undefined,
      }),
    select: (response) => response.data,
    staleTime: 60_000,
  });
}

export function useItems(search?: string) {
  return useQuery({
    queryKey: ['items', 'select', search],
    queryFn: () => api.get<Paginated<Item>>('/items', { perPage: 100, search: search || undefined }),
    select: (response) => response.data,
    staleTime: 60_000,
  });
}

/**
 * Datas SEMPRE na data civil de America/Sao_Paulo — em UTC o dia vira às 21h
 * de Brasília, e uma baixa lançada à noite entraria pré-datada para amanhã.
 */
export function todayISO(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

/**
 * Competência corrente em `AAAA-MM`, no fuso do parque.
 *
 * Mora aqui, junto do `todayISO`, porque o erro é o mesmo: das 21h à
 * meia-noite do último dia do mês, o UTC já virou. Uma tela que calculasse
 * isso com `toISOString()` abriria no mês SEGUINTE — e a API, que usa o fuso
 * de Brasília, recusaria como competência futura.
 */
export function currentCompetence(): string {
  return todayISO().slice(0, 7);
}

/** Primeiro/último dia do mês corrente em `YYYY-MM-DD`, no fuso de SP. */
export function monthRange(): { from: string; to: string } {
  const [year, month] = todayISO().split('-').map(Number) as [number, number];
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` };
}
